/**
 * Which pages show one FAQ — the routing rule, written once.
 *
 * An FAQ has no page of its own (`lib/url-inventory.js`, UNPUBLISHED_COLLECTIONS).
 * It is composed onto other pages, and four templates each apply their own
 * filter to the collection:
 *
 *   /assessment/<slug>/         category "Assessment" **and** attached to that
 *                               Assessment (src/assessment/assessment.11tydata.js)
 *   /service/<slug>/            attached to that Service, whatever the category
 *                               (src/service/service.11tydata.js, ADR-0011)
 *   /counselling-psychotherapy/ category "Counselling Page"
 *                               (src/counselling-psychotherapy/…11tydata.js)
 *   /questions/                 category "General", plus every Live FAQ that
 *                               none of the three above shows
 *                               (src/questions/questions.11tydata.js)
 *
 * Those four filters stay where they are — each page also has an *order*, and
 * the order is that page's business. What lives here is the complement: given
 * one FAQ, the list of pages that will show it. `/questions`'s catch-all reads
 * it to know what "no other page" means, and the admin panel reads it to say
 * "Appears on: …" in the editor and to open a preview on the first host page
 * (ADR-0016). The two agree because they call the same function.
 *
 * Consequences of the rule worth knowing:
 *
 *   - An Assessment-category FAQ attached to nothing lands on /questions. Eight
 *     Live FAQs are like this by design — the general questions about
 *     assessments ("Do I need a doctor's referral to book an assessment?").
 *   - A General FAQ attached to a Service appears on both that Service's page
 *     and /questions ("What are your operating hours?" is attached to an
 *     Assessment, which a General FAQ's category does not activate, so it is
 *     /questions only).
 *   - A Counselling Page FAQ never reaches /questions, attached or not.
 *   - An attachment to a draft or archived Assessment or Service is not a
 *     placement: the loader has already dropped it, and here a reference is
 *     only honoured when the target is in the Live list handed in.
 *
 * References arrive in two shapes. The site's records (lib/webflow-content.js)
 * resolve `assessment`/`service` to Live records with an `id`; the panel holds
 * raw `fieldData`, where they are arrays of ids. Both are accepted.
 */

/** The category names are the option names templates filter on — never renamed. */
const CATEGORY = {
  assessment: "Assessment",
  service: "Service",
  general: "General",
  counselling: "Counselling Page",
};

const QUESTIONS_PAGE = Object.freeze({ url: "/questions/", title: "Questions" });
const COUNSELLING_PAGE = Object.freeze({ url: "/counselling-psychotherapy/", title: "Counselling & Psychotherapy" });

/** A reference as the loader resolves it (`{id}`) or as fieldData holds it (an id). */
function refId(ref) {
  if (typeof ref === "string") return ref;
  return ref && typeof ref === "object" && typeof ref.id === "string" ? ref.id : null;
}

/** The Live targets one FAQ's reference list points at, in the target list's order. */
function attachedTo(refs, targets) {
  const wanted = new Set((Array.isArray(refs) ? refs : []).map(refId).filter(Boolean));
  return (targets || []).filter((target) => target && wanted.has(target.id));
}

/**
 * The pages that show this FAQ, in the order the site would list them:
 * Assessment pages, then Service pages, then the counselling page, then
 * /questions. Never empty — an FAQ nothing else claims is on /questions.
 *
 * @param {object} faq a Live FAQ record (`category` as the option *name*,
 *   `assessment`/`service` as arrays of records or ids)
 * @param {{id: string, slug: string, name?: string}[]} assessments the Live Assessments
 * @param {{id: string, slug: string, name?: string}[]} services the Live Services
 * @returns {{url: string, title: string}[]}
 */
function placementsOf(faq, assessments, services) {
  const category = faq && typeof faq.category === "string" ? faq.category : null;
  const pages = [];

  if (category === CATEGORY.assessment) {
    for (const a of attachedTo(faq.assessment, assessments)) {
      pages.push({ url: `/assessment/${a.slug}/`, title: a.name || a.slug });
    }
  }
  for (const s of attachedTo(faq && faq.service, services)) {
    pages.push({ url: `/service/${s.slug}/`, title: s.name || s.slug });
  }
  if (category === CATEGORY.counselling) {
    pages.push({ ...COUNSELLING_PAGE });
  }
  if (category === CATEGORY.general || pages.length === 0) {
    pages.push({ ...QUESTIONS_PAGE });
  }
  return pages;
}

/**
 * True when /questions is the only page that shows this FAQ *and* it is not
 * there by category — the "no other page claims it" half of /questions's list.
 * The General ones are the other half, and the page adds them itself.
 */
function isUnplaced(faq, assessments, services) {
  if (faq && faq.category === CATEGORY.general) return false;
  const pages = placementsOf(faq, assessments, services);
  return pages.length === 1 && pages[0].url === QUESTIONS_PAGE.url;
}

module.exports = { CATEGORY, COUNSELLING_PAGE, QUESTIONS_PAGE, isUnplaced, placementsOf };
